/**
 * Focus filters for a project detail view. Purely client-side: narrows the
 * already-fetched blocks by branch, block type and agent. When any filter is
 * active the parent re-derives its stats/breakdowns/billing from the subset.
 */

export type TypeFilter = 'all' | 'human' | 'agent';

export interface Filters {
  branch: string; // 'all' or a branch label
  type: TypeFilter;
  agent: string; // 'all' or an agent name
}

export const EMPTY_FILTERS: Filters = { branch: 'all', type: 'all', agent: 'all' };

export function filtersActive(f: Filters): boolean {
  return f.branch !== 'all' || f.type !== 'all' || f.agent !== 'all';
}

interface Props {
  value: Filters;
  onChange: (next: Filters) => void;
  branches: string[];
  agents: string[];
}

export function FilterBar({ value, onChange, branches, agents }: Props) {
  const hasAgents = agents.length > 0;
  const active = filtersActive(value);

  return (
    <div className="flex flex-wrap items-center gap-3 border border-border-light rounded-comfort p-3 bg-white">
      <span className="mono-label text-black/50">filter</span>

      <Select
        label="branch"
        value={value.branch}
        onChange={(branch) => onChange({ ...value, branch })}
        options={['all', ...branches]}
      />

      <div className="flex items-center gap-1.5">
        <span className="mono-label text-black/50">type</span>
        <div className="flex items-center gap-1">
          {(['all', 'human', 'agent'] as TypeFilter[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => onChange({ ...value, type: t })}
              className={`rounded-sharp px-2.5 py-1 font-display text-caption transition-colors ${
                value.type === t
                  ? 'bg-midnight text-white'
                  : 'bg-glass-dark text-black hover:bg-black/10'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {hasAgents && (
        <Select
          label="agent"
          value={value.agent}
          onChange={(agent) => onChange({ ...value, agent })}
          options={['all', ...agents]}
        />
      )}

      {active && (
        <button
          type="button"
          onClick={() => onChange(EMPTY_FILTERS)}
          className="mono-label text-midnight hover:underline ml-auto"
        >
          clear
        </button>
      )}
    </div>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="mono-label text-black/50">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent border border-border-light rounded-sharp px-2 py-1 font-mono text-[12px] text-black max-w-[180px]"
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
}
